import { createAuthRequestInit } from '../utils/auth';
import { API_BASE, getAbsoluteApiUrl } from '../config/api';

export type PlatformOverview = {
  total: number;
  allocated: number;
  available: number;
  pending: number;
};

export type UserCreditRow = {
  userId: string;
  client: string;
  email: string;
  plan: string | null;
  allocated: number;
  used: number;
  remaining: number;
  usagePct: number;
};

export async function fetchPlatformOverview(): Promise<PlatformOverview> {
  const res = await fetch(`${API_BASE}/credits/overview`, createAuthRequestInit());
  if (!res.ok) throw new Error('Failed to load overview');
  const json = await res.json();
  return json.data.overview as PlatformOverview;
}

export async function addPlatformCredits(payload: { amount: number; provider?: string; purchaseReference?: string; notes?: string }): Promise<PlatformOverview> {
  const res = await fetch(
    `${API_BASE}/credits/platform/add`,
    createAuthRequestInit(
      {
        method: 'POST',
        body: JSON.stringify(payload)
      },
      true
    )
  );
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || 'Failed to add credits');
  }
  const json = await res.json();
  return json.data.overview as PlatformOverview;
}

export async function listUserCredits(params?: { client?: string; plan?: string; usage?: 'high'|'medium'|'low' }): Promise<UserCreditRow[]> {
  const url = new URL(getAbsoluteApiUrl('/credits/users'));
  if (params?.client) url.searchParams.set('client', params.client);
  if (params?.plan) url.searchParams.set('plan', params.plan);
  if (params?.usage) url.searchParams.set('usage', params.usage);
  const res = await fetch(url.toString(), createAuthRequestInit());
  if (!res.ok) throw new Error('Failed to load user credits');
  const json = await res.json();
  return json.data.users as UserCreditRow[];
}

export async function allocateCredits(payload: { userId: string; amount: number; reason?: string; expiresAt?: string }): Promise<{ allocation: any; overview: PlatformOverview }> {
  const res = await fetch(
    `${API_BASE}/credits/allocate`,
    createAuthRequestInit(
      {
        method: 'POST',
        body: JSON.stringify(payload)
      },
      true
    )
  );
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || 'Failed to allocate credits');
  }
  const json = await res.json();
  return json.data as { allocation: any; overview: PlatformOverview };
}

export type UserCredits = {
  allocated: number;
  used: number;
  remaining: number;
};

export async function getCurrentUserCredits(): Promise<UserCredits> {
  const res = await fetch(`${API_BASE}/credits/me`, createAuthRequestInit());
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || 'Failed to load user credits');
  }
  const json = await res.json();
  return json.data.credits as UserCredits;
}

export async function exportUserAllocations(params: {
  userId: string;
  startDate: string;
  endDate: string;
}): Promise<Blob> {
  const url = new URL(getAbsoluteApiUrl('/credits/export-allocations'));
  url.searchParams.set('userId', params.userId);
  url.searchParams.set('startDate', params.startDate);
  url.searchParams.set('endDate', params.endDate);
  const res = await fetch(url.toString(), createAuthRequestInit());
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || 'Failed to export allocations');
  }
  return await res.blob();
}

export async function exportUserUsedCredits(params: {
  userId: string;
  startDate: string;
  endDate: string;
}): Promise<Blob> {
  const url = new URL(getAbsoluteApiUrl('/credits/export-used'));
  url.searchParams.set('userId', params.userId);
  url.searchParams.set('startDate', params.startDate);
  url.searchParams.set('endDate', params.endDate);
  const res = await fetch(url.toString(), createAuthRequestInit());
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || 'Failed to export used credits');
  }
  return await res.blob();
}


